import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, Typography, Box, Paper, Grid, TextField, 
  Button, FormControlLabel, Checkbox, Slider, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Card, CardContent, List, ListItem, ListItemText,
  CircularProgress, Alert, Snackbar, IconButton, Rating,
  Accordion, AccordionSummary, AccordionDetails, Tabs, Tab
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncIcon from '@mui/icons-material/Sync';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import axios from 'axios';
import moment from 'moment';

const ComparativeAnalysis = () => {
  const { callIds } = useParams();
  const navigate = useNavigate();
  const audioRefs = useRef({});
  
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [syncPlayback, setSyncPlayback] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [masterPlaybackTime, setMasterPlaybackTime] = useState(0);
  const [playbackTimes, setPlaybackTimes] = useState({});
  const [durations, setDurations] = useState({});
  const [callScores, setCallScores] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [debugMode, setDebugMode] = useState(false);
  
  // Parse call IDs from URL
  const callIdsArray = callIds.split(',').map(id => parseInt(id));
  
  // Load calls data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get multiple calls for comparison
        const callsResponse = await axios.post('/api/qa/comparative/calls', {
          callIds: callIdsArray
        });
        
        // Check if the response is successful
        if (!callsResponse.data.success) {
          throw new Error(callsResponse.data.error || 'Failed to load calls');
        }
        
        const loadedCalls = callsResponse.data.data;
        
        // Validate that we got an array
        if (!Array.isArray(loadedCalls)) {
          throw new Error('Invalid response format: expected array of calls');
        }
        
        // Add authentication token to audio URLs
        const token = localStorage.getItem('token');
        const callsWithAuthUrls = loadedCalls.map(call => ({
          ...call,
          audioUrl: call.audioUrl ? `${call.audioUrl}?token=${token}` : null
        }));
        
        setCalls(callsWithAuthUrls);
        
        if (loadedCalls.length > 0) {
          // Get QA metrics for the tenant of the first call
          const metricsResponse = await axios.get(`/api/qa/metrics/${loadedCalls[0].tenant_id}`);
          
          // Check if the metrics response is successful
          if (!metricsResponse.data.success) {
            throw new Error(metricsResponse.data.error || 'Failed to load QA metrics');
          }
          
          const metricsData = metricsResponse.data.data;
          
          // Extract criteria from the metrics response
          const loadedMetrics = metricsData?.summary?.criteria ? Object.entries(metricsData.summary.criteria).map(([key, value]) => ({
            id: key,
            name: value.name || key,
            description: value.description || '',
            maxScore: value.maxScore || 100
          })) : [];
          
          // Validate that we got an array
          if (!Array.isArray(loadedMetrics)) {
            throw new Error('Invalid metrics response format: expected array of metrics');
          }
          
          // Sort metrics to match CallAnalysis order exactly
          const QA_CRITERIA_ORDER = [
            'Communication Skills',      // communication
            'Product/Service Knowledge', // knowledge  
            'Process Adherence',         // process
            'Customer Service',          // customerService
            'Compliance & Legal'         // compliance
          ];
          
          const sortedMetrics = loadedMetrics.sort((a, b) => {
            const indexA = QA_CRITERIA_ORDER.indexOf(a.name);
            const indexB = QA_CRITERIA_ORDER.indexOf(b.name);
            
            // If both are in the order array, sort by their position
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            // If only one is in the order array, prioritize it
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            // If neither is in the order array, sort alphabetically
            return a.name.localeCompare(b.name);
          });
          
          setMetrics(sortedMetrics);
          
          // Initialize scores for each call
          const initialCallScores = loadedCalls.map(call => {
            console.log('=== COMPARATIVE ANALYSIS DEBUG ===');
            console.log('Call ID:', call.id);
            console.log('Call QA Scores:', call.CallQAScores);
            console.log('Available metrics:', loadedMetrics.map(m => ({ id: m.id, name: m.name })));
            
            // Add comprehensive debugging for JSONB structure
            console.log('=== DETAILED JSONB DEBUG ===');
            console.log('Call ID:', call.id);
            console.log('Call QA Scores:', call.CallQAScores);
            
            if (call.CallQAScores && call.CallQAScores.length > 0) {
              const qaScore = call.CallQAScores[0];
              console.log('Full QA Score Object:', qaScore);
              console.log('QA Score metrics field:', qaScore.metrics);
              console.log('QA Score metrics type:', typeof qaScore.metrics);
              console.log('QA Score metrics keys:', qaScore.metrics ? Object.keys(qaScore.metrics) : 'null/undefined');
              
              // Try to understand the structure
              if (qaScore.metrics) {
                console.log('Metrics detailed breakdown:');
                for (const [key, value] of Object.entries(qaScore.metrics)) {
                  console.log(`  ${key}:`, value, `(type: ${typeof value})`);
                  if (typeof value === 'object' && value !== null) {
                    console.log(`    ${key} keys:`, Object.keys(value));
                    console.log(`    ${key} full object:`, value);
                  }
                }
              }
            }
            
            console.log('Available metrics:', metrics);
            
            // Convert existing scores (if any) into the format we need
            const scores = sortedMetrics.map(metric => {
              // Get the best QA evaluation for comparison (prioritize original over comparative)
              let bestEvaluation = null;
              if (call.CallQAScores && call.CallQAScores.length > 0) {
                // First try to find a non-comparative evaluation (original/AI)
                bestEvaluation = call.CallQAScores.find(score => 
                  score.metrics?.evaluation_type !== 'comparative'
                );
                // If no original evaluation, use the first available (could be comparative)
                if (!bestEvaluation) {
                  bestEvaluation = call.CallQAScores[0];
                }
              }
              
              // Extract score for this metric from the JSONB metrics field
              const metricsData = bestEvaluation?.metrics || {};
              let metricScore = 0;
              let metricComment = '';
              
              // Map metric names to category keys used in the JSONB structure
              const categoryKeyMap = {
                'Communication Skills': 'communication',
                'Product/Service Knowledge': 'knowledge', 
                'Process Adherence': 'process',
                'Customer Service': 'customerService',
                'Compliance & Legal': 'compliance'
              };
              
              const categoryKey = categoryKeyMap[metric.name];
              
              // Extract score from different possible locations in JSONB
              if (categoryKey) {
                // Try categories first (manual evaluations)
                if (metricsData.categories && metricsData.categories[categoryKey] !== undefined) {
                  const rawScore = metricsData.categories[categoryKey];
                  
                  // Convert raw score to percentage based on max scores from CallAnalysis
                  const maxScores = {
                    'communication': 20,
                    'knowledge': 20,
                    'process': 25,
                    'customerService': 20,
                    'compliance': 15
                  };
                  
                  const maxScore = maxScores[categoryKey] || 100;
                  metricScore = Math.round((rawScore / maxScore) * 100);
                }
                // Try detailed_scores (AI evaluations)
                else if (metricsData.detailed_scores && metricsData.detailed_scores[categoryKey]) {
                  const scoreData = metricsData.detailed_scores[categoryKey];
                  console.log(`Detailed score data for ${categoryKey}:`, scoreData);
                  console.log(`Score data keys:`, typeof scoreData === 'object' ? Object.keys(scoreData) : 'not an object');
                  
                  // Extract score from the detailed score object
                  if (typeof scoreData === 'object') {
                    console.log(`Checking score extraction for ${categoryKey}:`, {
                      percentage: scoreData.percentage,
                      score: scoreData.score,
                      maxScore: scoreData.maxScore,
                      value: scoreData.value,
                      rating: scoreData.rating
                    });
                    
                    if (scoreData.percentage !== undefined) {
                      metricScore = scoreData.percentage;
                    } else if (scoreData.score !== undefined && scoreData.maxScore !== undefined) {
                      metricScore = Math.round((scoreData.score / scoreData.maxScore) * 100);
                    } else if (scoreData.score !== undefined) {
                      metricScore = scoreData.score;
                    } else if (scoreData.value !== undefined) {
                      metricScore = scoreData.value;
                    } else if (scoreData.rating !== undefined) {
                      metricScore = scoreData.rating;
                    }
                  } else if (typeof scoreData === 'number') {
                    metricScore = scoreData;
                  }
                }
              }
              
              // Extract comment from multiple possible sources
              console.log(`=== COMMENT EXTRACTION DEBUG for ${metric.name} (${categoryKey}) ===`);
              console.log('Best evaluation:', bestEvaluation);
              console.log('Metrics data:', metricsData);
              
              // Priority 1: Try to extract from detailed_scores (most specific)
              if (categoryKey && metricsData.detailed_scores && metricsData.detailed_scores[categoryKey]) {
                const scoreData = metricsData.detailed_scores[categoryKey];
                console.log(`Detailed score data for ${categoryKey}:`, scoreData);
                if (typeof scoreData === 'object') {
                  metricComment = scoreData.feedback || scoreData.comment || scoreData.notes || scoreData.description || scoreData.explanation || '';
                  console.log(`Found comment in detailed_scores: "${metricComment}"`);
                }
              }
              
              // Priority 2: Try to extract from metric_comments field (Call Review submissions)
              if (!metricComment && metricsData.metric_comments) {
                if (typeof metricsData.metric_comments === 'object' && metricsData.metric_comments[categoryKey]) {
                  metricComment = metricsData.metric_comments[categoryKey];
                  console.log(`Found comment in metric_comments object: "${metricComment}"`);
                } else if (typeof metricsData.metric_comments === 'string') {
                  // Try pattern matching for string format
                  if (metricsData.metric_comments.includes(metric.name)) {
                    const metricPattern = new RegExp(`${metric.name}[:\\s]*([^\\n\\r]+)`, 'i');
                    const match = metricsData.metric_comments.match(metricPattern);
                    if (match && match[1]) {
                      metricComment = match[1].trim();
                      console.log(`Found comment in metric_comments string: "${metricComment}"`);
                    }
                  }
                }
              }
              
              // Priority 2.5: Try to extract from categories object directly (might be where simple numbers are stored)
              if (!metricComment && metricsData.categories) {
                console.log(`Checking categories for ${categoryKey}:`, metricsData.categories);
                // Sometimes comments might be stored as separate fields in categories
                if (metricsData.categories[categoryKey + '_comment']) {
                  metricComment = metricsData.categories[categoryKey + '_comment'];
                  console.log(`Found comment in categories with _comment suffix: "${metricComment}"`);
                } else if (metricsData.categories[categoryKey + '_notes']) {
                  metricComment = metricsData.categories[categoryKey + '_notes'];
                  console.log(`Found comment in categories with _notes suffix: "${metricComment}"`);
                }
              }
              
              // Priority 3: Try to extract from category_comments if available
              if (!metricComment && metricsData.category_comments && metricsData.category_comments[categoryKey]) {
                metricComment = metricsData.category_comments[categoryKey];
                console.log(`Found comment in category_comments: "${metricComment}"`);
              }
              
              // Priority 4: Try to extract from individual metric comments in JSONB
              if (!metricComment && metricsData.comments && metricsData.comments[categoryKey]) {
                metricComment = metricsData.comments[categoryKey];
                console.log(`Found comment in comments: "${metricComment}"`);
              }
              
              // Priority 5: Try to parse general notes for metric-specific comments using patterns
              if (!metricComment && bestEvaluation?.notes && typeof bestEvaluation.notes === 'string') {
                const notesText = bestEvaluation.notes;
                console.log(`Trying to extract from notes: "${notesText}"`);
                
                // Try different patterns to extract metric-specific comments
                const patterns = [
                  new RegExp(`${metric.name}[:\\s]*([^\\n\\r;.]+)`, 'i'),
                  new RegExp(`${categoryKey}[:\\s]*([^\\n\\r;.]+)`, 'i'),
                  new RegExp(`\\b${metric.name.toLowerCase()}\\b[:\\s]*([^\\n\\r;.]+)`, 'i')
                ];
                
                for (const pattern of patterns) {
                  const match = notesText.match(pattern);
                  if (match && match[1] && match[1].trim().length > 5) { // Ensure meaningful content
                    metricComment = match[1].trim();
                    console.log(`Extracted comment using pattern: "${metricComment}"`);
                    break;
                  }
                }
              }
              
              // Priority 6: Comprehensive search through all possible JSONB locations
              if (!metricComment) {
                console.log(`Performing comprehensive search for ${metric.name} comments...`);
                console.log('Full metricsData structure:', JSON.stringify(metricsData, null, 2));
                
                // Search through all possible nested structures
                const searchPaths = [
                  `${categoryKey}_comment`,
                  `${categoryKey}_notes`,
                  `${categoryKey}_feedback`,
                  categoryKey,
                  metric.name.toLowerCase().replace(/[^a-z]/g, ''),
                  metric.id
                ];
                
                // Search recursively through the metrics data
                function searchForComment(obj, path = '') {
                  if (typeof obj === 'string' && obj.length > 0 && obj.length < 200) {
                    console.log(`Found potential comment at ${path}: "${obj}"`);
                    return obj;
                  }
                  
                  if (typeof obj === 'object' && obj !== null) {
                    for (const [key, value] of Object.entries(obj)) {
                      const newPath = path ? `${path}.${key}` : key;
                      
                      // Check if this key matches any of our search terms
                      if (searchPaths.some(term => key.toLowerCase().includes(term.toLowerCase()))) {
                        const result = searchForComment(value, newPath);
                        if (result) return result;
                      }
                    }
                  }
                  
                  return null;
                }
                
                const foundComment = searchForComment(metricsData);
                if (foundComment) {
                  metricComment = foundComment;
                  console.log(`Found comment via comprehensive search: "${metricComment}"`);
                }
              }
              
              // Priority 7: DON'T use general notes as fallback unless they're very short and specific
              // This prevents generic notes like "TEST2" from appearing as metric comments
              
              console.log(`Final comment for ${metric.name}: "${metricComment}"`);
              console.log('=== END COMMENT EXTRACTION DEBUG ===');
              
              console.log(`Extracting ${metric.name} (${categoryKey}): score=${metricScore}, comment available=${!!metricComment}`);
              
              const scoreResult = {
                metricId: metric.id,
                score: metricScore,
                comment: metricComment
              };
              
              console.log(`Score result for ${metric.name}:`, scoreResult);
              return scoreResult;
            });
            
            return {
              callId: call.id,
              scores,
              overallScore: call.qa_score || 0,
              callNotes: call.notes || ''
            };
          });
          
          console.log('=== FINAL CALL SCORES DEBUG ===');
          console.log('Initial call scores:', JSON.stringify(initialCallScores, null, 2));
          
          setCallScores(initialCallScores);
          
          // Set comparison notes if any call has them
          const callWithComparativeNotes = loadedCalls.find(call => call.comparativeNotes);
          if (callWithComparativeNotes) {
            setComparisonNotes(callWithComparativeNotes.comparativeNotes);
          }
        }
      } catch (error) {
        console.error('Error loading calls data:', error);
        let errorMessage = 'Failed to load calls data';
        
        if (error.response?.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (error.response?.status === 403) {
          errorMessage = 'Access denied. You do not have permission to view this data.';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [callIds]);
  
  // Audio handling
  useEffect(() => {
    // Set up audio event listeners
    const newPlaybackTimes = {};
    const newDurations = {};
    
    const handleTimeUpdate = (event) => {
      const callId = parseInt(event.target.dataset.callId);
      
      setPlaybackTimes(prev => ({
        ...prev,
        [callId]: event.target.currentTime
      }));
      
      // Update master playback time if this is the selected call
      if (callId === calls[selectedTab]?.id) {
        setMasterPlaybackTime(event.target.currentTime);
      }
      
      // If in sync mode, sync other players
      if (syncPlayback && isPlaying) {
        syncOtherPlayers(callId, event.target.currentTime);
      }
    };
    
    const handleDurationChange = (event) => {
      const callId = parseInt(event.target.dataset.callId);
      
      setDurations(prev => ({
        ...prev,
        [callId]: event.target.duration
      }));
    };
    
    const handleEnded = (event) => {
      const callId = parseInt(event.target.dataset.callId);
      
      // If master player ended, stop all
      if (callId === calls[selectedTab]?.id) {
        setIsPlaying(false);
        pauseAllPlayers();
      }
    };
    
    calls.forEach(call => {
      if (audioRefs.current[call.id]) {
        const audio = audioRefs.current[call.id];
        
        // Initial values
        newPlaybackTimes[call.id] = audio.currentTime || 0;
        newDurations[call.id] = audio.duration || 0;
        
        // Clean up old listeners
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('durationchange', handleDurationChange);
        audio.removeEventListener('ended', handleEnded);
        
        // Add new listeners with the properly scoped functions
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('durationchange', handleDurationChange);
        audio.addEventListener('ended', handleEnded);
      }
    });
    
    setPlaybackTimes(newPlaybackTimes);
    setDurations(newDurations);
    
    // Cleanup function
    return () => {
      calls.forEach(call => {
        if (audioRefs.current[call.id]) {
          const audio = audioRefs.current[call.id];
          audio.removeEventListener('timeupdate', handleTimeUpdate);
          audio.removeEventListener('durationchange', handleDurationChange);
          audio.removeEventListener('ended', handleEnded);
        }
      });
    };
  }, [calls, selectedTab, syncPlayback, isPlaying]);
  
  const handleTimeUpdate = (event) => {
    const callId = parseInt(event.target.dataset.callId);
    
    setPlaybackTimes(prev => ({
      ...prev,
      [callId]: event.target.currentTime
    }));
    
    // Update master playback time if this is the selected call
    if (callId === calls[selectedTab]?.id) {
      setMasterPlaybackTime(event.target.currentTime);
    }
    
    // If in sync mode, sync other players
    if (syncPlayback && isPlaying) {
      syncOtherPlayers(callId, event.target.currentTime);
    }
  };
  
  const handleDurationChange = (event) => {
    const callId = parseInt(event.target.dataset.callId);
    
    setDurations(prev => ({
      ...prev,
      [callId]: event.target.duration
    }));
  };
  
  const handleEnded = (event) => {
    const callId = parseInt(event.target.dataset.callId);
    
    // If master player ended, stop all
    if (callId === calls[selectedTab]?.id) {
      setIsPlaying(false);
      pauseAllPlayers();
    }
  };
  
  const syncOtherPlayers = (sourceCallId, time) => {
    Object.entries(audioRefs.current).forEach(([callId, audio]) => {
      callId = parseInt(callId);
      if (callId !== sourceCallId && audio && Math.abs(audio.currentTime - time) > 0.5) {
        audio.currentTime = time;
      }
    });
  };
  
  const togglePlayPause = () => {
    if (isPlaying) {
      pauseAllPlayers();
    } else {
      playAllPlayers();
    }
    setIsPlaying(!isPlaying);
  };
  
  const playAllPlayers = () => {
    if (syncPlayback) {
      // Play all at same position
      const masterTime = playbackTimes[calls[selectedTab]?.id] || 0;
      
      Object.entries(audioRefs.current).forEach(([callId, audio]) => {
        if (audio) {
          audio.currentTime = masterTime;
          audio.play().catch(e => console.error("Error playing audio:", e));
        }
      });
    } else {
      // Just play the selected tab
      const selectedCallId = calls[selectedTab]?.id;
      if (selectedCallId && audioRefs.current[selectedCallId]) {
        audioRefs.current[selectedCallId].play().catch(e => console.error("Error playing audio:", e));
      }
    }
  };
  
  const pauseAllPlayers = () => {
    Object.values(audioRefs.current).forEach(audio => {
      if (audio) {
        audio.pause();
      }
    });
  };
  
  const handleSeek = (event, newValue) => {
    // Prevent seeking if no audio is loaded
    if (!calls[selectedTab]?.id || !audioRefs.current[calls[selectedTab].id]) {
      return;
    }
    
    setMasterPlaybackTime(newValue);
    
    // Update all players if in sync mode, otherwise just the selected one
    if (syncPlayback) {
      Object.values(audioRefs.current).forEach(audio => {
        if (audio && audio.duration) {
          // Only seek if audio is loaded and duration is available
          audio.currentTime = Math.min(newValue, audio.duration);
        }
      });
    } else {
      const selectedCallId = calls[selectedTab]?.id;
      if (selectedCallId && audioRefs.current[selectedCallId] && audioRefs.current[selectedCallId].duration) {
        audioRefs.current[selectedCallId].currentTime = Math.min(newValue, audioRefs.current[selectedCallId].duration);
      }
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    
    // Update master playback time to match the newly selected tab
    const selectedCallId = calls[newValue]?.id;
    if (selectedCallId && audioRefs.current[selectedCallId]) {
      setMasterPlaybackTime(audioRefs.current[selectedCallId].currentTime);
    }
  };
  
  // Format time in MM:SS
  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };
  
  // Handle score changes
  const handleScoreChange = (callIndex, metricId, value) => {
    setCallScores(prevScores => {
      const newScores = [...prevScores];
      
      // Find the metric in the call's scores array
      const metricIndex = newScores[callIndex].scores.findIndex(s => s.metricId === metricId);
      
      if (metricIndex !== -1) {
        // Update the score
        newScores[callIndex].scores[metricIndex].score = value;
        
        // Recalculate overall score
        calculateOverallScore(callIndex, newScores[callIndex].scores);
      }
      
      return newScores;
    });
  };
  
  // Handle comment changes
  const handleCommentChange = (callIndex, metricId, comment) => {
    setCallScores(prevScores => {
      const newScores = [...prevScores];
      
      // Find the metric in the call's scores array
      const metricIndex = newScores[callIndex].scores.findIndex(s => s.metricId === metricId);
      
      if (metricIndex !== -1) {
        // Update the comment
        newScores[callIndex].scores[metricIndex].comment = comment;
      }
      
      return newScores;
    });
  };
  
  // Handle call notes changes
  const handleCallNotesChange = (callIndex, notes) => {
    setCallScores(prevScores => {
      const newScores = [...prevScores];
      newScores[callIndex].callNotes = notes;
      return newScores;
    });
  };
  
  // Removed handleSendToProvidersChange - no longer needed
  
  // Calculate overall score for a call
  const calculateOverallScore = (callIndex, scores) => {
    if (!scores || scores.length === 0) return;
    
    let totalWeight = 0;
    let weightedSum = 0;
    
    scores.forEach(score => {
      const metric = metrics.find(m => m.id === score.metricId);
      if (metric && score.score > 0) {
        const weight = metric.weight || 1;
        totalWeight += weight;
        weightedSum += score.score * weight;
      }
    });
    
    // Calculate weighted average
    const calculatedScore = totalWeight > 0 
      ? Math.round(weightedSum / totalWeight) 
      : 0;
    
    // Update the overall score
    setCallScores(prevScores => {
      const newScores = [...prevScores];
      newScores[callIndex].overallScore = calculatedScore;
      return newScores;
    });
  };
  
  // Submit comparative analysis
  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      // Prepare submission data
      const submissionData = {
        callScores
      };
      
      // Debug what we're sending
      console.log('=== FRONTEND SUBMISSION DEBUG ===');
      console.log('Call scores being submitted:', JSON.stringify(callScores, null, 2));
      console.log('Full submission data:', JSON.stringify(submissionData, null, 2));
      
      // Submit to API
      await axios.post('/api/qa/comparative/analysis', submissionData);
      
      setNotification({
        open: true,
        message: 'Comparative analysis submitted successfully! The scores and notes have been saved to the individual calls and can be viewed in the regular QA Review section.',
        severity: 'success'
      });
      
      // Navigate back to list after delay to allow user to read message
      setTimeout(() => {
        navigate('/qa-review');
      }, 4000);
    } catch (error) {
      console.error('Error submitting comparative analysis:', error);
      setNotification({
        open: true,
        message: 'Failed to submit comparative analysis: ' + (error.response?.data?.message || error.message),
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle notification close
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };
  
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (!calls || calls.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">No calls found for comparison</Alert>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/qa-review')}
          sx={{ mt: 2 }}
        >
          Back to QA Review
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/qa-review')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          Comparative QA Analysis - {calls.length} Calls
        </Typography>
      </Box>
      
      {/* Call recordings section */}
      <Paper sx={{ mb: 4, p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
          <Tabs 
            value={selectedTab} 
            onChange={handleTabChange} 
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flexGrow: 1 }}
          >
            {calls.map((call, index) => (
              <Tab 
                key={call.id} 
                                    label={`Call ${index + 1} - ${call.agent?.name || 'Unknown'}`} 
                id={`call-tab-${index}`}
                aria-controls={`call-tabpanel-${index}`}
              />
            ))}
          </Tabs>
          
          <Button
            startIcon={syncPlayback ? <SyncIcon /> : <NotInterestedIcon />}
            onClick={() => setSyncPlayback(!syncPlayback)}
            sx={{ mx: 2 }}
            color={syncPlayback ? "primary" : "inherit"}
          >
            {syncPlayback ? "Sync Playback" : "Independent"}
          </Button>
        </Box>
        
        {/* Audio controls */}
        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={togglePlayPause} color="primary" size="large">
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            
            <Box sx={{ width: '100%', ml: 2, mr: 2 }}>
              <Slider
                value={masterPlaybackTime}
                onChange={handleSeek}
                max={Math.max(...Object.values(durations)) || 100}
                aria-label="Call position"
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              {formatTime(masterPlaybackTime)} / {formatTime(durations[calls[selectedTab]?.id])}
            </Typography>
          </Box>
          
          {/* Hidden audio elements */}
          <Box sx={{ display: 'none' }}>
            {calls.map((call) => (
              call.audioUrl ? (
                <audio 
                  key={call.id} 
                  ref={el => audioRefs.current[call.id] = el} 
                  src={call.audioUrl} 
                  preload="metadata" 
                  data-call-id={call.id}
                />
              ) : null
            ))}
          </Box>
        </Box>
        
        {/* Call details */}
        <Box sx={{ p: 2 }}>
          {calls.map((call, index) => (
            <Box 
              key={call.id} 
              role="tabpanel"
              hidden={selectedTab !== index}
              id={`call-tabpanel-${index}`}
              aria-labelledby={`call-tab-${index}`}
            >
              {selectedTab === index && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>Call Details</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Date/Time" 
                          secondary={(() => {
                            // Use same date logic as CallReview for consistency
                            if (call.created_at) return moment(call.created_at).format('YYYY-MM-DD HH:mm:ss');
                            if (call.createdAt) return moment(call.createdAt).format('YYYY-MM-DD HH:mm:ss');
                            if (call.call_date) return moment(call.call_date).format('YYYY-MM-DD HH:mm:ss');
                            
                            // Fallback: try to extract from unique_id if it contains timestamp
                            if (call.unique_id && call.unique_id.includes('_')) {
                              const parts = call.unique_id.split('_');
                              const timestampPart = parts[parts.length - 1];
                              if (timestampPart && timestampPart.length >= 10) {
                                try {
                                  const timestamp = parseInt(timestampPart);
                                  if (timestamp > 1000000000) { // Valid timestamp
                                    return moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
                                  }
                                } catch (e) {
                                  console.log('Could not parse timestamp from unique_id');
                                }
                              }
                            }
                            
                            return 'N/A';
                          })()} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Phone Number" 
                          secondary={call.phone} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Duration" 
                          secondary={`${Math.floor(call.duration / 60)}:${String(call.duration % 60).padStart(2, '0')}`} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Disposition" 
                          secondary={call.disposition} 
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>Agent Information</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Agent Name" 
                          secondary={call.agent?.name || 'Unknown'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Agent ID" 
                          secondary={call.agent?.vici_id || 'Unknown'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="User Group" 
                          secondary={call.agent?.user_group || 'Unknown'} 
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              )}
            </Box>
          ))}
        </Box>
      </Paper>
      
      {/* Comparative QA Scoring */}
      <Paper sx={{ mb: 4 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Comparative Quality Assessment</Typography>
          <Typography variant="body2" color="text.secondary">
            Compare and score multiple calls side-by-side. Expand each metric to view all calls.
          </Typography>
        </Box>
        
        {metrics.map((metric) => (
          <Accordion key={metric.id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">{metric.name}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {metric.description}
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Call</TableCell>
                      <TableCell align="center">Score (0-100)</TableCell>
                      <TableCell>Comments</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {calls.map((call, callIndex) => {
                      const callScore = callScores[callIndex];
                      const scoreItem = callScore?.scores.find(s => s.metricId === metric.id);
                      
                      // Debug logging for comment values
                      if (metric.name === 'Communication Skills') {
                        console.log(`=== COMMENT DISPLAY DEBUG for Call ${callIndex + 1} ===`);
                        console.log('Call Score:', callScore);
                        console.log('Score Item:', scoreItem);
                        console.log('Comment value:', scoreItem?.comment);
                        console.log('Metric ID:', metric.id);
                      }
                      
                      return (
                        <TableRow key={`${call.id}-${metric.id}`}>
                          <TableCell>
                            <Typography variant="body2">
                              Call {callIndex + 1} - {call.agent?.name || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(() => {
                                if (call.created_at) return moment(call.created_at).format('YYYY-MM-DD HH:mm');
                                if (call.createdAt) return moment(call.createdAt).format('YYYY-MM-DD HH:mm');
                                if (call.call_date) return moment(call.call_date).format('YYYY-MM-DD HH:mm');
                                
                                // Extract from unique_id if possible
                                if (call.unique_id && call.unique_id.includes('_')) {
                                  const parts = call.unique_id.split('_');
                                  const timestampPart = parts[parts.length - 1];
                                  if (timestampPart && timestampPart.length >= 10) {
                                    try {
                                      const timestamp = parseInt(timestampPart);
                                      if (timestamp > 1000000000) {
                                        return moment(timestamp).format('YYYY-MM-DD HH:mm');
                                      }
                                    } catch (e) {}
                                  }
                                }
                                
                                return 'N/A';
                              })()}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Rating
                              name={`rating-${call.id}-${metric.id}`}
                              value={scoreItem?.score / 20 || 0}
                              precision={0.5}
                              onChange={(e, newValue) => 
                                handleScoreChange(callIndex, metric.id, newValue * 20)
                              }
                              max={5}
                            />
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {scoreItem?.score || 0}/100
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              multiline
                              rows={2}
                              fullWidth
                              placeholder="Enter comments for this metric..."
                              value={scoreItem?.comment || ''}
                              onChange={(e) => 
                                handleCommentChange(callIndex, metric.id, e.target.value)
                              }
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
      
      {/* Overall Assessment */}
      <Paper sx={{ mb: 4, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Overall Assessment
        </Typography>
        
        <TableContainer sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Call</TableCell>
                <TableCell>Overall Score</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.map((call, callIndex) => (
                <TableRow key={call.id}>
                  <TableCell>
                    <Typography variant="body2">
                      Call {callIndex + 1} - {call.agent?.name || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(() => {
                        if (call.created_at) return moment(call.created_at).format('YYYY-MM-DD HH:mm');
                        if (call.createdAt) return moment(call.createdAt).format('YYYY-MM-DD HH:mm');
                        if (call.call_date) return moment(call.call_date).format('YYYY-MM-DD HH:mm');
                        
                        // Extract from unique_id if possible
                        if (call.unique_id && call.unique_id.includes('_')) {
                          const parts = call.unique_id.split('_');
                          const timestampPart = parts[parts.length - 1];
                          if (timestampPart && timestampPart.length >= 10) {
                            try {
                              const timestamp = parseInt(timestampPart);
                              if (timestamp > 1000000000) {
                                return moment(timestamp).format('YYYY-MM-DD HH:mm');
                              }
                            } catch (e) {}
                          }
                        }
                        
                        return 'N/A';
                      })()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">
                      {callScores[callIndex]?.overallScore || 0}/100
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <TextField
                      multiline
                      rows={2}
                      fullWidth
                      placeholder="Enter call-specific notes..."
                      value={callScores[callIndex]?.callNotes || ''}
                      onChange={(e) => handleCallNotesChange(callIndex, e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>

                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        

        
        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
            endIcon={<SendIcon />}
          >
            {submitting ? 'Submitting...' : 'Submit Comparative Analysis'}
          </Button>
        </Box>
      </Paper>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.severity === 'success' ? 8000 : 6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ComparativeAnalysis; 